import {usePersonalMemos} from '../../personalMemos/PersonalMemoProvider';
import {createPortal} from 'react-dom';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import '../../../../css/common/chat/ChatHeaderOverflowMenu.css';

// Shared by chat headers and topic rows; stays inside its owning dialog's focus boundary.
/**
 * @param {{
 *   items?: Array<{key: string, label: React.ReactNode, icon?: string, purpose?: 'state', hidden?: boolean, disabled?: boolean, onClick?: () => void}>,
 *   ariaLabel?: string, className?: string, iconClassName?: string, disabled?: boolean, memoTarget?: any, memoName?: string, contextMenuPosition?: {x: number, y: number}|null, onContextMenuClose?: () => void, contextAnchorRef?: any
 * }} props
 */
export default function ChatHeaderOverflowMenu({ items = [], ariaLabel = 'メニュー', className = '', iconClassName = 'fa-solid fa-ellipsis-vertical', disabled = false, memoTarget = undefined, memoName = '', contextMenuPosition = undefined, onContextMenuClose = undefined, contextAnchorRef = undefined }) {
  const [triggerOpen, setOpen] = useState(false);
  const contextMode = contextMenuPosition !== undefined;
  const open = contextMode ? Boolean(contextMenuPosition) : triggerOpen;
  const [position, setPosition] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const memos=usePersonalMemos();
  const target=memoTarget===undefined?memos?.current:memoTarget;
  const memoItem=memos&&target?{key:'personal-memo',label:'メモ',icon:'fa-note-sticky',onClick:()=>memos.open(target,memoName)}:null;
  const sharedMemoItem=memos?.actor==='admin'&&target?{key:'admin-shared-memo',label:'共有メモ',icon:'fa-note-sticky',onClick:()=>memos.open(target,memoName,'admin_shared')}:null;
  const available = [...(items || []),...(sharedMemoItem?[sharedMemoItem]:[]),...(memoItem?[memoItem]:[])].filter((item) => item && !item.hidden);
  const stateItems = available.filter((item) => item.purpose === 'state');
  const otherItems = available.filter((item) => item.purpose !== 'state');
  const visible = [...stateItems, ...otherItems];
  const close = useCallback((restoreFocus = false) => {
    if (contextMode) onContextMenuClose?.(); else setOpen(false);
    if (restoreFocus) (contextMode ? contextAnchorRef?.current : triggerRef.current)?.focus();
  }, [contextMode, onContextMenuClose, contextAnchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = contextMode ? {right:contextMenuPosition.x,top:contextMenuPosition.y,bottom:contextMenuPosition.y} : triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const margin = 8;
    const left = Math.max(margin, Math.min(trigger.right - menu.width, window.innerWidth - menu.width - margin));
    const below = trigger.bottom + margin;
    const top = below + menu.height <= window.innerHeight - margin ? below : Math.max(margin, trigger.top - menu.height - margin);
    setPosition({ left, top });
    menuRef.current.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open, contextMode, contextMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!ref.current?.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (!ref.current?.contains(event.target)) return;
      // Window capture precedes DsModal's document capture: Esc closes only this menu.
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation(); close(true); return;
      }
      if (event.key === 'Tab') { close(true); return; }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      const options = Array.from(menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)'));
      if (!options.length) return;
      const index = options.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options[next].focus();
    };
    const onLayoutChange = (event) => {
      if (!menuRef.current?.contains(event.target)) close(true);
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
    };
  }, [close, open]);

  if (!visible.length || (contextMode && !open)) return null;
  const content = (
    <div className={`chat-header-overflow ${className}`.trim()} ref={ref} onClick={contextMode ? (event) => event.stopPropagation() : undefined}>
      {!contextMode && <button ref={triggerRef} type="button" disabled={disabled}
        className="chat-header-icon-button chat-header-overflow__trigger"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => { if (!open && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); setOpen(true); } }}
        aria-label={ariaLabel} title={ariaLabel} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}>
        <i className={iconClassName} aria-hidden="true" />
      </button>}
      {open ? (
        <div ref={menuRef} id={menuId} style={position || { left: 0, top: 0 }} className="chat-header-overflow__menu ds-compact-action-menu" role="menu" aria-label={ariaLabel}>
          {visible.map((item, index) => (
            <React.Fragment key={item.key}>
            {index === stateItems.length && stateItems.length > 0 && <div className="chat-header-overflow__separator" role="separator" />}
            <button type="button" role="menuitem" tabIndex={-1} disabled={item.disabled}
              className={`chat-header-overflow__item${item.purpose === 'state' ? ' chat-header-overflow__item--state' : ''}`}
              onClick={() => { close(true); item.onClick?.(); }}>
              <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
  return contextMode ? createPortal(content,document.body) : content;
}
